import { gql, useMutation } from "@apollo/client";
import { Button, Form, Input, message, Modal } from "antd";
import FormItem from "antd/es/form/FormItem";
import TextArea from "antd/lib/input/TextArea";
import ResponsibleDesignFirmsDropdown from "components/responsible-design-firms";
import { ErrorMessages } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { MUTATION_CREATE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { useContext, useState } from "react";
import DependedCompanyUserDropdownComponent from "components/responsible-designers";
import {
  isProjectInIntegrationMode,
  validatorForFieldValue
} from "utils/utils";
import GCReviewersDropdown from "components/gc-reveiewer-dropdown";
import { useProjectParticipants } from "hooks/project-participants";

function CreateDesignPackageComponent(props: {
  setDrawerOpen: Function;
  showDrawerOpen: boolean;
  modelTitle: string;
  onDesignPackageCreated: any;
}) {
  const [form] = Form.useForm();
  const { setDrawerOpen, showDrawerOpen, modelTitle, onDesignPackageCreated } =
    props;
  const [isLoading, setLoading] = useState(false);
  const { gqlClientForProject, tokenRetrievalState, projectDetails } =
    useContext(ProjectContext);
  const selectedDesignFirmId = Form.useWatch("design_firm", form);
  const token = tokenRetrievalState?.token;

  const [createDesignPackage] = useMutation<any>(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),

    {
      client: gqlClientForProject
    }
  );

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const { projectParticipants } = useProjectParticipants(isIntegrationMode);

  const onFinish = async (values: any) => {
    const instancePayload: any = {
      number: values?.number,
      title: values?.title,
      description: values?.description,
      design_firm: values?.design_firm,
      gc_representative: values?.gc_representative
    };

    if (values?.responsible_designer) {
      const foundUser =
        projectParticipants?.allProjectUsersMap[values?.responsible_designer];
      if (foundUser?.type === "poc") {
        instancePayload.responsible_designer_unregistered =
          values?.responsible_designer;
        instancePayload.responsible_designer = null;
      } else {
        instancePayload.responsible_designer_unregistered = null;
        instancePayload.responsible_designer = values?.responsible_designer;
      }
    }

    const variables = {
      feature_type: "DESIGN_PACKAGE",
      feature_instances: [instancePayload]
    };

    setLoading(true);

    const creatResponse: any = await createDesignPackage({
      variables
    });

    setLoading(false);

    if (creatResponse?.data?.insert_feature_instances_multi?.ids?.length) {
      onDesignPackageCreated(
        values?.title,
        creatResponse?.data?.insert_feature_instances_multi?.ids[0]
      );
    } else {
      message.error("Unable to create design package.");
    }
  };

  return (
    <Modal
      title={modelTitle}
      width={420}
      style={{
        right: 0,
        bottom: 0,
        top: 40,
        padding: 0,
        position: "absolute"
      }}
      bodyStyle={{ minHeight: "calc(100vh - 96px)" }}
      footer={null}
      open={showDrawerOpen}
      onCancel={() => {
        setDrawerOpen(false);
      }}
      destroyOnClose
      data-testid="index-modal-create-design-package"
    >
      <div>
        <Form
          preserve={false}
          form={form}
          scrollToFirstError
          layout="vertical"
          onFinish={onFinish}
        >
          <FormItem
            name="number"
            label="Number"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.designPackgeNumber
              },
              {
                validator: (_, value) => {
                  return validatorForFieldValue(
                    "number",
                    value?.trim(),
                    "DESIGN_PACKAGE",
                    token,
                    ErrorMessages.DesignPackageNumberMsg
                  );
                }
              }
            ]}
          >
            <Input />
          </FormItem>
          <FormItem
            name="title"
            label="Design Package Title"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.designPackgeTitle
              },
              {
                validator: (_, value) => {
                  return validatorForFieldValue(
                    "title",
                    value?.trim(),
                    "DESIGN_PACKAGE",
                    token,
                    ErrorMessages.DesignPackageTitleMsg
                  );
                }
              }
            ]}
          >
            <Input />
          </FormItem>

          <FormItem name="description" label="Description ">
            <TextArea />
          </FormItem>
          <Form.Item label="Responsible Design Firm" name="design_firm">
            <ResponsibleDesignFirmsDropdown />
          </Form.Item>
          <Form.Item label="Responsible Designer" name="responsible_designer">
            <DependedCompanyUserDropdownComponent
              projectParticipantType="designReviewers"
              selectedCompanyFieldId={selectedDesignFirmId}
              companyNotFoundContent="Please select a Design Firm"
            />
          </Form.Item>

          <Form.Item label="GC Representative" name="gc_representative">
            <GCReviewersDropdown />
          </Form.Item>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              disabled={isLoading}
              onClick={() => {
                setDrawerOpen(false);
              }}
              data-testid="index-button-cancel"
            >
              Cancel
            </Button>
            <Button
              loading={isLoading}
              disabled={isLoading}
              htmlType="submit"
              type="primary"
              data-testid="index-button-create-design-package"
            >
              Create Design Package
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}

export default CreateDesignPackageComponent;
